import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { ArrowLeft, Plus, Trash2, Phone, User, History, Upload, Download, AlertCircle, MoreVertical } from 'lucide-react';
import { CreateCompetition, createJudge, createMultipleJudges, getCompetitionById, getJudges, deleteJudge } from '../services/apiServices';

export const JudgeManagement = () => {
    const navigate = useNavigate();
    const { addJudge, updateCompetition } = useCompetition();
    const location = useLocation();
    const [competition, setCompetition] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [newJudge, setNewJudge] = useState({
        firstname: '',
        lastname: '',
        phone: '',
        _id: -1
    });

    const [allJudges, setAllJudges] = useState([]);
    const [competitionJudges, setCompetitionJudges] = useState([]);
    const [showPreviousJudges, setShowPreviousJudges] = useState(false);
    const [selectedPreviousJudges, setSelectedPreviousJudges] = useState([]);

    // New states for delete functionality
    const [deletingJudgeId, setDeletingJudgeId] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [judgeToDelete, setJudgeToDelete] = useState(null);

    // Validation states
    const [formErrors, setFormErrors] = useState({});
    const [importErrors, setImportErrors] = useState([]);
    const [showImportErrors, setShowImportErrors] = useState(false);

    const fileInputRef = useRef(null);

    console.log(competition, 'competition');

    useEffect(() => {
        async function fetchCompetition() {
            try {
                let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
                const response = await getCompetitionById(location.state?._id);

                if (!response || response === "false") {
                    return;
                } else {
                    const competitionData = await response.json();
                    console.log(competitionData, 'hvdfhdhb');

                    setCompetition(competitionData)
                }
            } catch (error) {
                console.error("Error fetching climbers:", error);
            }
        }
        if (location.state?._id) {
            fetchCompetition();
        }
    }, [location.state]);

    useEffect(() => {
        async function fetchJudges() {
            try {
                let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
                const judgeResponse = await getJudges(organiserId);

                if (!judgeResponse || judgeResponse === "false") {
                    return;
                } else {
                    const judgesData = await judgeResponse.json();
                    console.log(judgesData, 'judgesData');

                    if (judgesData) {
                        setAllJudges(judgesData);
                        if (competition?.judges) {
                            const assignedJudges = judgesData.filter(judge =>
                                competition.judges.includes(judge._id)
                            );
                            setCompetitionJudges(assignedJudges);
                        }
                    }
                    setRefresh(false);
                }
            } catch (error) {
                console.error("Error fetching judge:", error);
            }
        }

        fetchJudges();
    }, [refresh, competition]);

    // Delete judge functions
    const handleDeleteJudgeClick = (judge, event) => {
        event.stopPropagation(); // Prevent checkbox selection
        setJudgeToDelete(judge);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!judgeToDelete) return;

        setDeletingJudgeId(judgeToDelete._id);
        try {
            const organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo"))?.organizerId;
            if (!organiserId) {
                alert('Organizer ID not found');
                return;
            }

            const response = await deleteJudge(organiserId, judgeToDelete._id);

            if (response && response.ok) {
                // Remove judge from allJudges state
                setAllJudges(prev => prev.filter(judge => judge._id !== judgeToDelete._id));

                // Remove judge from selectedPreviousJudges if selected
                setSelectedPreviousJudges(prev => prev.filter(id => id !== judgeToDelete._id));

                alert('Judge deleted successfully');
            } else {
                throw new Error('Failed to delete judge');
            }
        } catch (error) {
            console.error('Error deleting judge:', error);
            alert('Failed to delete judge. Please try again.');
        } finally {
            setDeletingJudgeId(null);
            setShowDeleteDialog(false);
            setJudgeToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setJudgeToDelete(null);
    };

    // Validation functions
    const validateForm = () => {
        const errors = {};

        // First Name validation (unchanged)
        if (!newJudge.firstname.trim()) {
            errors.firstname = 'First name is required';
        } else if (newJudge.firstname.trim().length < 2) {
            errors.firstname = 'First name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(newJudge.firstname.trim())) {
            errors.firstname = 'First name can only contain letters and spaces';
        }

        // Last Name validation (unchanged)
        if (!newJudge.lastname.trim()) {
            errors.lastname = 'Last name is required';
        } else if (newJudge.lastname.trim().length < 2) {
            errors.lastname = 'Last name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(newJudge.lastname.trim())) {
            errors.lastname = 'Last name can only contain letters and spaces';
        }

        // Phone validation - CORRECTED
        const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
        const cleanPhone = newJudge.phone.replace(/[\s\-\(\)]/g, '');

        if (!newJudge.phone.trim()) {
            errors.phone = 'Phone number is required';
        } else if (!phoneRegex.test(cleanPhone)) {
            errors.phone = 'Please enter a valid phone number';
        } else {
            // Check for duplicate phone in existing judges
            const existingJudge = allJudges.find(
                judge => judge.phone.replace(/[\s\-\(\)]/g, '') === cleanPhone
            );
            if (existingJudge) {
                errors.phone = 'This phone number already exists!';
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateCSVData = (judges) => {
        const errors = [];

        // Track phones in current import for duplicates
        const importPhones = new Set();

        judges.forEach((judge, index) => {
            const rowNumber = index + 1;
            const rowErrors = [];

            // First Name validation (unchanged)
            if (!judge.firstname?.trim()) {
                rowErrors.push('First name is required');
            } else if (judge.firstname.trim().length < 2) {
                rowErrors.push('First name must be at least 2 characters');
            } else if (!/^[a-zA-Z\s]+$/.test(judge.firstname.trim())) {
                rowErrors.push('First name can only contain letters and spaces');
            }

            // Last Name validation (unchanged)
            if (!judge.lastname?.trim()) {
                rowErrors.push('Last name is required');
            } else if (judge.lastname.trim().length < 2) {
                rowErrors.push('Last name must be at least 2 characters');
            } else if (!/^[a-zA-Z\s]+$/.test(judge.lastname.trim())) {
                rowErrors.push('Last name can only contain letters and spaces');
            }

            // Phone validation - CORRECTED
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
            const cleanPhone = judge.phone?.replace(/[\s\-\(\)]/g, '') || '';

            if (!judge.phone?.trim()) {
                rowErrors.push('Phone number is required');
            } else if (!phoneRegex.test(cleanPhone)) {
                rowErrors.push('Please enter a valid phone number');
            } else {
                // Check for duplicate phone in existing judges
                const existingJudge = allJudges.find(
                    j => j.phone.replace(/[\s\-\(\)]/g, '') === cleanPhone
                );
                if (existingJudge) {
                    rowErrors.push(`Phone number ${judge.phone} already exists in the system`);
                }

                // Check for duplicate phone within current import
                if (importPhones.has(cleanPhone)) {
                    rowErrors.push(`Duplicate phone number in import`);
                } else {
                    importPhones.add(cleanPhone);
                }
            }

            if (rowErrors.length > 0) {
                errors.push({
                    row: rowNumber,
                    data: `${judge.firstname || 'N/A'}, ${judge.lastname || 'N/A'}, ${judge.phone || 'N/A'}`,
                    errors: rowErrors
                });
            }
        });

        return errors;
    };


    const handleAddJudge = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";

            const response = await createJudge(organiserId, newJudge);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const createdJudge = await response.json();
            console.log('Created Judge:', createdJudge);

            if (createdJudge && createdJudge._id) {
                const updatedJudges = [...(competition.judges || []), createdJudge._id];

                const competitionUpdateData = {
                    ...competition,
                    judges: updatedJudges,
                    _id: competition._id
                };

                const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

                if (!updateResponse.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const apiData = await updateResponse.json();
                console.log('API Response:', apiData);

                setCompetition(apiData);
                setCompetitionJudges(prev => [...prev, createdJudge]);
                setRefresh(true);

                setNewJudge({
                    firstname: '',
                    lastname: '',
                    phone: '',
                    _id: -1
                });
                setFormErrors({});
                console.log('Judge added and competition updated successfully');
            }
        } catch (error) {
            console.error('Error adding judge:', error);
        }
    };

    const handleAddPreviousJudges = async () => {
        try {
            if (selectedPreviousJudges.length === 0) return;

            const updatedJudges = [
                ...(competition.judges || []),
                ...selectedPreviousJudges
            ];

            const competitionUpdateData = {
                ...competition,
                judges: updatedJudges,
                _id: competition._id
            };

            const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

            if (!updateResponse.ok) {
                throw new Error(`HTTP error! status: ${updateResponse.status}`);
            }

            const apiData = await updateResponse.json();
            console.log('API Response:', apiData);

            setCompetition(apiData);

            const selectedJudgeData = allJudges.filter(judge =>
                selectedPreviousJudges.includes(judge._id)
            );
            setCompetitionJudges(prev => [...prev, ...selectedJudgeData]);

            setShowPreviousJudges(false);
            setSelectedPreviousJudges([]);

            console.log('Previous judges added successfully');
        } catch (error) {
            console.error('Error adding previous judges:', error);
        }
    };

    const handleRemoveJudge = async (judgeId) => {
        try {
            const updatedJudges = competition.judges.filter(id => id !== judgeId);

            const competitionUpdateData = {
                ...competition,
                judges: updatedJudges,
                _id: competition._id
            };

            const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

            if (!updateResponse.ok) {
                throw new Error(`HTTP error! status: ${updateResponse.status}`);
            }

            const apiData = await updateResponse.json();
            console.log('API Response:', apiData);

            setCompetition(apiData);
            setCompetitionJudges(prev => prev.filter(judge => judge._id !== judgeId));

            console.log('Judge removed successfully');
        } catch (error) {
            console.error('Error removing judge:', error);
        }
    };

    const handleImportCSV = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target?.result;
                const lines = text.split('\n').filter(line => line.trim());

                if (lines.length === 0) {
                    alert('CSV file is empty.');
                    return;
                }

                // Check if first line contains headers
                const startIndex = lines[0].toLowerCase().includes('firstname') ||
                    lines[0].toLowerCase().includes('lastname') ||
                    lines[0].toLowerCase().includes('phone') ? 1 : 0;

                const importedJudges = lines.slice(startIndex).map((line, index) => {
                    const [firstname, lastname, phone] = line.split(',').map(s => s.trim());
                    return { firstname, lastname, phone, originalRow: index + startIndex + 1 };
                }).filter(j => j.firstname && j.lastname && j.phone); // Only include rows with all data

                console.log('Importing judges:', importedJudges);

                if (importedJudges.length === 0) {
                    alert('No valid judge data found in the CSV file. Please check the format.');
                    return;
                }

                // Validate CSV data
                const validationErrors = validateCSVData(importedJudges);
                if (validationErrors.length > 0) {
                    setImportErrors(validationErrors);
                    setShowImportErrors(true);
                    return;
                }

                let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
                const response = await createMultipleJudges(organiserId, importedJudges);

                console.log('Response status:', response.status);

                if (!response.ok) {
                    const errorData = await response.json();
                    console.error('Error response:', errorData);
                    alert(`Error: ${errorData.message || 'Failed to import judges'}`);
                    return;
                }

                const createdJudges = await response.json();
                console.log('Created judges:', createdJudges);

                if (createdJudges && createdJudges.length > 0) {
                    const newJudgeIds = createdJudges.map(judge => judge._id);

                    const updatedJudges = [...(competition.judges || []), ...newJudgeIds];

                    const competitionUpdateData = {
                        ...competition,
                        judges: updatedJudges,
                        _id: competition._id
                    };

                    const updateResponse = await CreateCompetition(competition._id, competitionUpdateData);

                    if (!updateResponse.ok) {
                        throw new Error(`HTTP error! status: ${updateResponse.status}`);
                    }

                    const apiData = await updateResponse.json();
                    setCompetition(apiData);
                    setCompetitionJudges(prev => [...prev, ...createdJudges]);
                }

                setRefresh(true);

                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

                alert(`Successfully imported ${importedJudges.length} judges!`);
                console.log('CSV import completed');
            } catch (error) {
                console.error('Error importing CSV:', error);
                alert(`Import failed: ${error.message}`);
            }
        };
        reader.readAsText(file);
    };

    const handleExportTemplate = () => {
        const csv = 'First Name,Last Name,Phone\nJohn,Smith,+1 (555) 123-4567\nJane,Doe,+1 (555) 987-6543';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'judges_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const compJudges = competition?.judges || [];

    const availablePreviousJudges =
        Array.isArray(allJudges)
            ? allJudges.filter(judge => !compJudges.includes(judge._id))
            : [];

    if (!competition) {
        return (
            <div className="min-h-screen p-4 md:p-6 max-w-2xl mx-auto">
                <Alert>
                    <AlertDescription>
                        Please create a competition first.
                    </AlertDescription>
                </Alert>
                <Button onClick={() => navigate('/create-round')} className="mt-4">
                    Create Competition
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-6 max-w-4xl mx-auto bg-gray-50">
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                        <ArrowLeft className="size-5" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Manage Judges</h1>
                        <p className="text-gray-600 mt-1">Add and manage competition judges</p>
                    </div>
                </div>

                {/* Competition Info */}
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-xl text-gray-900">Competition: {competition.name}</CardTitle>
                        <CardDescription className="text-gray-600">
                            {competitionJudges.length} judge{competitionJudges.length !== 1 ? 's' : ''} assigned to this competition
                        </CardDescription>
                    </CardHeader>
                </Card>

                {/* Add Judge Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="bg-white border-gray-200 shadow-sm">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg text-gray-900">Add New Judge</CardTitle>
                            <CardDescription className="text-gray-600">Create a new judge profile</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <Label htmlFor="firstName" className="text-gray-700 font-medium">First Name *</Label>
                                    <Input
                                        id="firstName"
                                        value={newJudge.firstname}
                                        onChange={(e) => {
                                            setNewJudge({ ...newJudge, firstname: e.target.value });
                                            if (formErrors.firstname) {
                                                setFormErrors(prev => ({ ...prev, firstname: '' }));
                                            }
                                        }}
                                        placeholder="John"
                                        className={`border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 ${formErrors.firstname ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                                            }`}
                                    />
                                    {formErrors.firstname && (
                                        <p className="text-red-600 text-sm flex items-center gap-1">
                                            <AlertCircle className="size-4" />
                                            {formErrors.firstname}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lastName" className="text-gray-700 font-medium">Last Name *</Label>
                                    <Input
                                        id="lastName"
                                        value={newJudge.lastname}
                                        onChange={(e) => {
                                            setNewJudge({ ...newJudge, lastname: e.target.value });
                                            if (formErrors.lastname) {
                                                setFormErrors(prev => ({ ...prev, lastname: '' }));
                                            }
                                        }}
                                        placeholder="Smith"
                                        className={`border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 ${formErrors.lastname ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                                            }`}
                                    />
                                    {formErrors.lastname && (
                                        <p className="text-red-600 text-sm flex items-center gap-1">
                                            <AlertCircle className="size-4" />
                                            {formErrors.lastname}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phoneNumber" className="text-gray-700 font-medium">Phone Number *</Label>
                                <Input
                                    id="phoneNumber"
                                    type="number"
                                    value={newJudge.phone}
                                    onChange={(e) => {
                                        setNewJudge({ ...newJudge, phone: e.target.value });
                                        if (formErrors.phone) {
                                            setFormErrors(prev => ({ ...prev, phone: '' }));
                                        }
                                    }}
                                    placeholder="+1 (555) 123-4567"
                                    className={`border-gray-300 focus:border-blue-500 focus:ring-blue-500 h-11 ${formErrors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                                        }`}
                                />
                                {formErrors.phone && (
                                    <p className="text-red-600 text-sm flex items-center gap-1">
                                        <AlertCircle className="size-4" />
                                        {formErrors.phone}
                                    </p>
                                )}
                            </div>
                            <Button
                                onClick={handleAddJudge}
                                disabled={!newJudge.firstname || !newJudge.lastname || !newJudge.phone}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-11"
                            >
                                <Plus className="size-4 mr-2" />
                                Add New Judge
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="bg-white border-gray-200 shadow-sm">
                        <CardHeader className="pb-4">
                            <CardTitle className="text-lg text-gray-900">Add Existing Judges</CardTitle>
                            <CardDescription className="text-gray-600">Select from previous judges</CardDescription>
                        </CardHeader>
                        <CardContent className="flex items-center justify-center h-full">
                            <Button
                                onClick={() => setShowPreviousJudges(true)}
                                variant="outline"
                                className="w-full h-20 border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 text-gray-700 font-medium"
                            >
                                <History className="size-5 mr-2" />
                                Select Previous Judges
                            </Button>
                        </CardContent>
                    </Card>
                </div>

                {/* Import/Export Section */}
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="pb-4">
                        <CardTitle className="text-lg text-gray-900">Import from CSV</CardTitle>
                        <CardDescription className="text-gray-600">
                            Upload a CSV file with columns: First Name, Last Name, Phone
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            onChange={handleImportCSV}
                            className="hidden"
                        />
                        <div className="flex gap-4">
                            <Button
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-11"
                            >
                                <Upload className="size-4 mr-2" />
                                Import CSV
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleExportTemplate}
                                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-11"
                            >
                                <Download className="size-4 mr-2" />
                                Download Template
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Judge List */}
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between pb-4">
                        <div>
                            <CardTitle className="text-xl text-gray-900">Competition Judges</CardTitle>
                            <CardDescription className="text-gray-600">
                                Judges assigned to this competition ({competitionJudges.length})
                            </CardDescription>
                        </div>
                        {competitionJudges.length > 0 && (
                            <Button
                                variant="outline"
                                onClick={() => setShowPreviousJudges(true)}
                                className="flex items-center gap-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                <History className="size-4" />
                                Add More Judges
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent>
                        {competitionJudges.length === 0 ? (
                            <div className="text-center py-12">
                                <User className="size-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 mb-4 text-lg">No judges assigned to this competition yet</p>
                                <div className="flex gap-3 justify-center">
                                    <Button
                                        onClick={() => setShowPreviousJudges(true)}
                                        variant="outline"
                                        className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                                    >
                                        <History className="size-4 mr-2" />
                                        Select from Previous Judges
                                    </Button>
                                    <Button
                                        onClick={() => fileInputRef.current?.click()}
                                        variant="outline"
                                        className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                                    >
                                        <Upload className="size-4 mr-2" />
                                        Import CSV
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {competitionJudges.map((judge) => (
                                    <div
                                        key={judge._id}
                                        className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center justify-center size-10 bg-blue-100 rounded-full">
                                                <User className="size-5 text-blue-600" />
                                            </div>
                                            <div className="space-y-1">
                                                <p className="font-semibold text-gray-900 text-lg">
                                                    {judge.firstname} {judge.lastname}
                                                </p>
                                                {judge.phone && (
                                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                                        <Phone className="size-3" />
                                                        <span>{judge.phone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleRemoveJudge(judge._id)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="size-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Previous Judges Dialog */}
                <Dialog open={showPreviousJudges} onOpenChange={setShowPreviousJudges}>
                    <DialogContent className="max-w-2xl bg-white border border-gray-200 shadow-xl">
                        <DialogHeader className="pb-4">
                            <DialogTitle className="text-xl text-gray-900 font-semibold">Select Previous Judges</DialogTitle>
                        </DialogHeader>
                        <div className="max-h-96 overflow-y-auto space-y-3">
                            {availablePreviousJudges.length === 0 ? (
                                <div className="text-center py-8">
                                    <User className="size-12 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg">No previous judges available to add</p>
                                </div>
                            ) : (
                                availablePreviousJudges.map((judge) => (
                                    <div
                                        key={judge._id}
                                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
                                    >
                                        <div className="flex items-center gap-3 flex-1"
                                            onClick={() => {
                                                const checked = !selectedPreviousJudges.includes(judge._id);
                                                if (checked) {
                                                    setSelectedPreviousJudges(prev => [...prev, judge._id]);
                                                } else {
                                                    setSelectedPreviousJudges(prev => prev.filter(id => id !== judge._id));
                                                }
                                            }}>
                                            <Checkbox
                                                checked={selectedPreviousJudges.includes(judge._id)}
                                            // onCheckedChange={(checked) => {
                                            //     if (checked) {
                                            //         setSelectedPreviousJudges(prev => [...prev, judge._id]);
                                            //     } else {
                                            //         setSelectedPreviousJudges(prev => prev.filter(id => id !== judge._id));
                                            //     }
                                            // }}
                                                className="text-blue-600 border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                                            />
                                            <div className="flex-1">
                                                <p className="font-medium text-gray-900 text-lg">
                                                    {judge.firstname} {judge.lastname}
                                                </p>
                                                {judge.phone && (
                                                    <p className="text-sm text-gray-600 mt-1">{judge.phone}</p>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => handleDeleteJudgeClick(judge, e)}
                                            disabled={deletingJudgeId === judge._id}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                            {deletingJudgeId === judge._id ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                                            ) : (
                                                <Trash2 className="size-4" />
                                            )}
                                        </Button>
                                    </div>
                                ))
                            )}
                        </div>
                        <DialogFooter className="pt-4 border-t border-gray-200">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowPreviousJudges(false);
                                    setSelectedPreviousJudges([]);
                                }}
                                className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAddPreviousJudges}
                                disabled={selectedPreviousJudges.length === 0}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
                            >
                                Add Selected Judges ({selectedPreviousJudges.length})
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Delete Judge Confirmation Dialog */}
                <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                    <DialogContent className="max-w-md bg-white border border-gray-200 shadow-xl">
                        <DialogHeader className="pb-4">
                            <DialogTitle className="text-xl text-gray-900 font-semibold flex items-center gap-2">
                                <Trash2 className="size-5 text-red-500" />
                                Delete Judge
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <p className="text-gray-700">
                                Are you sure you want to delete judge <span className="font-semibold">{judgeToDelete?.firstname} {judgeToDelete?.lastname}</span>?
                            </p>
                            <Alert className="bg-yellow-50 border-yellow-200">
                                <AlertDescription className="text-yellow-700">
                                    This action cannot be undone. The judge will be permanently removed from your account.
                                </AlertDescription>
                            </Alert>
                        </div>
                        <DialogFooter className="pt-4 border-t border-gray-200">
                            <Button
                                variant="outline"
                                onClick={handleCancelDelete}
                                disabled={deletingJudgeId}
                                className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmDelete}
                                disabled={deletingJudgeId}
                                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                            >
                                {deletingJudgeId ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="size-4" />
                                        Delete Judge
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Import Errors Dialog */}
                <Dialog open={showImportErrors} onOpenChange={setShowImportErrors}>
                    <DialogContent className="max-w-4xl bg-white border border-gray-200 shadow-xl">
                        <DialogHeader className="pb-4">
                            <DialogTitle className="text-xl text-gray-900 font-semibold flex items-center gap-2">
                                <AlertCircle className="size-6 text-red-500" />
                                CSV Import Errors
                            </DialogTitle>
                        </DialogHeader>
                        <div className="max-h-96 overflow-y-auto space-y-4">
                            <Alert className="bg-red-50 border-red-200">
                                <AlertDescription className="text-red-700">
                                    Please fix the following errors in your CSV file before importing:
                                </AlertDescription>
                            </Alert>
                            {importErrors.map((error, index) => (
                                <div key={index} className="p-4 border border-red-200 rounded-lg bg-red-50">
                                    <p className="font-medium text-red-800 mb-2">
                                        Row {error.row}: {error.data}
                                    </p>
                                    <ul className="list-disc list-inside space-y-1">
                                        {error.errors.map((err, errIndex) => (
                                            <li key={errIndex} className="text-red-700 text-sm">
                                                {err}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                        <DialogFooter className="pt-4 border-t border-gray-200">
                            <Button
                                onClick={() => setShowImportErrors(false)}
                                className="bg-red-600 hover:bg-red-700 text-white font-medium"
                            >
                                Close
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Navigation */}
                <div className="flex gap-3 pt-4">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/competitiondetails', { state: competition })}
                        className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-12"
                    >
                        Back
                    </Button>
                    <Button
                        onClick={() => navigate('/judge-assignment', { state: competition })}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium h-12"
                        disabled={competitionJudges.length === 0}
                    >
                        Continue to Assignment
                    </Button>
                </div>
            </div>
        </div>
    );
};